import React, { useMemo, useState } from 'react';
import { Holding, Currency, Loan, LiquidityEvent } from '../types';
import { Card, CardHeader, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { Modal } from './ui/Modal';
import { Building2, Calculator, Plus, Info } from 'lucide-react';
import { COUNTRIES, CURRENCIES, getDefaultCurrency } from '../utils/countries';
import { formatCurrency } from '../utils/currency';
import { calculateMonthlyPayment, calculateTotalInterest } from '../utils/loans';
import { LoanForm } from './LoanForm';
import { LiquidityEventForm } from './LiquidityEventForm';
interface RealEstateFormProps {
  onAdd: (holding: Omit<Holding, 'id' | 'createdAt' | 'history'>) => void;
  partnerNames: [string, string];
  loans: Loan[];
  liquidityEvents: LiquidityEvent[];
  holdings?: Holding[];
  onAddLoan?: (loan: Omit<Loan, 'id' | 'createdAt'>) => string | void;
  onAddEvent?: (event: Omit<LiquidityEvent, 'id'>) => string | void;
  baseCurrency?: Currency;
  baseCountry?: string;
  editingHolding?: Holding;
  onEdit?: (id: string, updates: Partial<Omit<Holding, 'id' | 'createdAt' | 'history'>>) => void;
  onCancelEdit?: () => void;
}
export function RealEstateForm({
  onAdd,
  partnerNames,
  loans,
  liquidityEvents,
  holdings = [],
  onAddLoan,
  onAddEvent,
  baseCurrency = 'USD',
  baseCountry = 'US',
  editingHolding,
  onEdit,
  onCancelEdit
}: RealEstateFormProps) {
  const [isExistingProperty, setIsExistingProperty] = useState(false);
  const [person, setPerson] = useState<string>(editingHolding?.person || partnerNames[0]);
  const [name, setName] = useState(editingHolding?.name || '');
  const [purchasePrice, setPurchasePrice] = useState(editingHolding?.purchasePrice?.toString() || '');
  const [currentValue, setCurrentValue] = useState(editingHolding?.amount?.toString() || '');
  const [appreciationRate, setAppreciationRate] = useState(editingHolding?.appreciationRate?.toString() || '3.0');
  const [capitalGainsTaxRate, setCapitalGainsTaxRate] = useState(editingHolding?.capitalGainsTaxRate?.toString() || '0');
  const [currency, setCurrency] = useState<Currency>(editingHolding?.currency || baseCurrency);
  const [country, setCountry] = useState<string>(editingHolding?.country || baseCountry);
  const [linkedLoanId, setLinkedLoanId] = useState<string>(editingHolding?.linkedLoanId || 'none');
  const [linkedEventId, setLinkedEventId] = useState<string>(editingHolding?.linkedEventIds?.[0] || 'none');
  // Closing / acquisition costs
  const [notaryCosts, setNotaryCosts] = useState('');
  const [stampDuty, setStampDuty] = useState('');
  const [transferTax, setTransferTax] = useState('');
  const [otherCosts, setOtherCosts] = useState(editingHolding?.closingCosts?.toString() || '');
  // Real estate strategy
  const [realEstateStrategy, setRealEstateStrategy] = useState<'hold' | 'sell'>(editingHolding?.realEstateStrategy || 'hold');
  const [plannedSaleYear, setPlannedSaleYear] = useState(editingHolding?.plannedSaleYear?.toString() || '10');
  // Funding source for closing costs
  const [closingCostsFundedFromHoldingId, setClosingCostsFundedFromHoldingId] = useState<string>(editingHolding?.closingCostsFundedFromHoldingId || 'none');
  const liquidHoldings = useMemo(() => {
    return holdings.filter(h => h.type === 'Liquid' && h.amount > 0);
  }, [holdings]);
  // Modal states
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const totalClosingCosts = (Number(notaryCosts) || 0) + (Number(stampDuty) || 0) + (Number(transferTax) || 0) + (Number(otherCosts) || 0);
  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const defaultCurr = getDefaultCurrency(newCountry);
    if (defaultCurr) {
      setCurrency(defaultCurr);
    }
  };
  // Live Equity Calculation
  const linkedLoan = useMemo(() => {
    return loans.find(l => l.id === linkedLoanId);
  }, [loans, linkedLoanId]);
  const outstandingLoanBalance = useMemo(() => {
    if (!linkedLoan) return 0;
    return Math.max(0, linkedLoan.totalAmount - linkedLoan.depositAmount);
  }, [linkedLoan]);
  // Linked event for rental/income
  const linkedEvent = useMemo(() => {
    return liquidityEvents.find(e => e.id === linkedEventId);
  }, [liquidityEvents, linkedEventId]);
  const currentValNum = Number(currentValue) || 0;
  const netEquity = Math.max(0, currentValNum - outstandingLoanBalance);
  const monthlyLoanPayment = linkedLoan ? calculateMonthlyPayment(linkedLoan) : 0;
  // Monthly income from linked event
  const monthlyEventIncome = useMemo(() => {
    if (!linkedEvent) return 0;
    if (linkedEvent.type === 'monthly') return linkedEvent.amount;
    if (linkedEvent.type === 'yearly') return linkedEvent.amount / 12;
    return 0;
  }, [linkedEvent]);
  const netMonthlyCashflow = monthlyEventIncome - monthlyLoanPayment;
  // Filter events suitable for linking (monthly/yearly income events)
  const linkableEvents = useMemo(() => {
    return liquidityEvents.filter(e => (e.isActive ?? true) && e.type !== 'one-time' && e.amount > 0);
  }, [liquidityEvents]);
  const activeLoans = loans.filter(l => l.isActive);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !currentValue || isNaN(Number(currentValue))) return;
    const finalClosingCosts = isExistingProperty ? 0 : totalClosingCosts;
    const finalFundedFrom = isExistingProperty ? undefined : closingCostsFundedFromHoldingId !== 'none' ? closingCostsFundedFromHoldingId : undefined;
    const updates = {
      person,
      name,
      amount: Number(currentValue),
      currency,
      type: 'Illiquid' as const,
      country,
      isRealEstate: true,
      purchasePrice: Number(purchasePrice) || 0,
      appreciationRate: Number(appreciationRate) || 0,
      capitalGainsTaxRate: Number(capitalGainsTaxRate) || 0,
      linkedLoanId: linkedLoanId !== 'none' ? linkedLoanId : undefined,
      linkedEventIds: linkedEventId !== 'none' ? [linkedEventId] : undefined,
      rentalIncome: monthlyEventIncome > 0 ? monthlyEventIncome : 0,
      closingCosts: finalClosingCosts > 0 ? finalClosingCosts : undefined,
      closingCostsFundedFromHoldingId: finalFundedFrom,
      realEstateStrategy,
      plannedSaleYear: realEstateStrategy === 'sell' ? Number(plannedSaleYear) || 10 : undefined,
      // Map to legacy drawdown fields for backward compat with projection engine
      drawdownAvailableYear: realEstateStrategy === 'sell' ? Number(plannedSaleYear) || 10 : 999,
      yieldsAfterDrawdown: realEstateStrategy === 'hold'
    };
    if (editingHolding && onEdit) {
      onEdit(editingHolding.id, updates);
    } else {
      onAdd(updates);
    }
    // Reset form
    if (!editingHolding) {
      setName('');
      setPurchasePrice('');
      setCurrentValue('');
      setAppreciationRate('3.0');
      setCapitalGainsTaxRate('0');
      setLinkedLoanId('none');
      setLinkedEventId('none');
      setClosingCostsFundedFromHoldingId('none');
      setNotaryCosts('');
      setStampDuty('');
      setTransferTax('');
      setOtherCosts('');
      setRealEstateStrategy('hold');
      setPlannedSaleYear('10');
    }
  };
  return <>
      <Card data-id="element-1171">
        <CardHeader title={editingHolding ? 'Edit Real Estate Asset' : 'Add Real Estate Asset'} subtitle={editingHolding ? 'Update property details and recalculate equity' : 'Track properties, link mortgages, and project equity'} data-id="element-1172" />
        <CardContent data-id="element-1173">
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1174">
            {!editingHolding && <div className="flex bg-slate-100 p-1 rounded-lg mb-2" data-id="element-1175">
                <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${!isExistingProperty ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsExistingProperty(false)} data-id="element-1176">
                  🆕 New Purchase
                </button>
                <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${isExistingProperty ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsExistingProperty(true)} data-id="element-1177">
                  🏠 Historical Purchase
                </button>
              </div>}

            {isExistingProperty && !editingHolding && <div className="bg-blue-50 border border-blue-100 text-blue-700 p-3 rounded-lg text-sm flex gap-2 items-start" data-id="element-1178">
                <Info className="w-4 h-4 mt-0.5 shrink-0" data-id="element-1179" />
                <p data-id="element-1180">
                  Enter the current market value. Sunk costs like closing fees
                  are ignored for historical purchases since they don't affect
                  your current liquid net worth.
                </p>
              </div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1181">
              <div data-id="element-1182">
                <Label data-id="element-1183">Property Name / Address</Label>
                <Input placeholder="e.g. 123 Main St, Rental Condo" value={name} onChange={e => setName(e.target.value)} required data-id="element-1184" />
              </div>
              <div data-id="element-1185">
                <Label data-id="element-1186">Owner</Label>
                <Select value={person} onChange={e => setPerson(e.target.value)} options={[{
                value: partnerNames[0],
                label: partnerNames[0]
              }, {
                value: partnerNames[1],
                label: partnerNames[1]
              }, {
                value: 'Joint',
                label: '🤝 Mutual (Joint)'
              }]} data-id="element-1187" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1188">
              <div data-id="element-1189">
                <Label data-id="element-1190">Country</Label>
                <Select searchable value={country} onChange={e => handleCountryChange(e.target.value)} options={COUNTRIES.map(c => ({
                value: c.code,
                label: `${c.flag} ${c.name}`
              }))} data-id="element-1191" />
              </div>
              <div data-id="element-1192">
                <Label data-id="element-1193">Currency</Label>
                <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
                value: c.code,
                label: `${c.flag} ${c.code} (${c.symbol})`
              }))} data-id="element-1194" />
              </div>
            </div>

            {/* Valuation */}
            <div className="border-t border-slate-100 pt-4" data-id="element-1195">
              <h4 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-1196">
                Valuation
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-id="element-1197">
                <div data-id="element-1198">
                  <Label data-id="element-1199">
                    {isExistingProperty ? 'Original Purchase Price' : 'Purchase Price'}
                  </Label>
                  <Input type="number" min="0" step="1000" placeholder="0" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} data-id="element-1200" />
                </div>
                <div data-id="element-1201">
                  <Label data-id="element-1202">
                    {isExistingProperty ? 'Current Market Value' : 'Current Value'}
                  </Label>
                  <Input type="number" min="0" step="1000" placeholder="0" value={currentValue} onChange={e => setCurrentValue(e.target.value)} required data-id="element-1203" />
                </div>
                <div data-id="element-1204">
                  <Label data-id="element-1205">Annual Apprec. (%)</Label>
                  <Input type="number" min="0" step="0.1" placeholder="3.0" value={appreciationRate} onChange={e => setAppreciationRate(e.target.value)} data-id="element-1206" />
                </div>
                <div data-id="element-1207">
                  <Label data-id="element-1208">Capital Gains Tax (%)</Label>
                  <Input type="number" min="0" step="0.1" placeholder="0.0" value={capitalGainsTaxRate} onChange={e => setCapitalGainsTaxRate(e.target.value)} data-id="element-1209" />
                </div>
              </div>
            </div>

            {/* Financing */}
            <div className="border-t border-slate-100 pt-4" data-id="element-1210">
              <h4 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-1211">
                Linked Mortgage / Loan
              </h4>
              <div className="flex items-end gap-3" data-id="element-1212">
                <div className="flex-1" data-id="element-1213">
                  <Label data-id="element-1214">Select Existing Loan</Label>
                  <Select value={linkedLoanId} onChange={e => setLinkedLoanId(e.target.value)} options={[{
                  value: 'none',
                  label: 'None (Paid off / Cash)'
                }, ...activeLoans.map(l => ({
                  value: l.id,
                  label: `${l.name} (${formatCurrency(l.totalAmount - l.depositAmount, l.currency)})`
                }))]} data-id="element-1215" />
                </div>
                {onAddLoan && <Button type="button" variant="secondary" className="shrink-0 mb-0" onClick={() => setShowLoanModal(true)} data-id="element-1216">
                    <Plus className="w-4 h-4 mr-1.5" data-id="element-1217" />
                    New Loan
                  </Button>}
              </div>
              {linkedLoan && <div className="mt-3 grid grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 text-sm" data-id="element-1218">
                  <div data-id="element-1219">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1220">Principal</p>
                    <p className="font-medium text-slate-900" data-id="element-1221">
                      {formatCurrency(outstandingLoanBalance, linkedLoan.currency)}
                    </p>
                  </div>
                  <div data-id="element-1222">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1223">
                      Monthly Payment
                    </p>
                    <p className="font-bold text-rose-600" data-id="element-1224">
                      -{formatCurrency(monthlyLoanPayment, linkedLoan.currency)}
                      /mo
                    </p>
                  </div>
                  <div data-id="element-1225">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1226">
                      Total Interest
                    </p>
                    <p className="font-medium text-rose-500" data-id="element-1227">
                      {formatCurrency(calculateTotalInterest(linkedLoan), linkedLoan.currency)}
                    </p>
                  </div>
                </div>}
            </div>

            {/* Linked Income / Cash Event */}
            <div className="border-t border-slate-100 pt-4" data-id="element-1228">
              <h4 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-1229">
                Linked Cash Income
              </h4>
              <div className="flex items-end gap-3" data-id="element-1230">
                <div className="flex-1" data-id="element-1231">
                  <Label data-id="element-1232">
                    Select Existing Cash Event (Rental Income, etc.)
                  </Label>
                  <Select value={linkedEventId} onChange={e => setLinkedEventId(e.target.value)} options={[{
                  value: 'none',
                  label: 'None (No recurring income)'
                }, ...linkableEvents.map(ev => ({
                  value: ev.id,
                  label: `${ev.name} (+${formatCurrency(ev.amount, ev.currency)}/${ev.type === 'monthly' ? 'mo' : 'yr'})`
                }))]} data-id="element-1233" />
                </div>
                {onAddEvent && <Button type="button" variant="secondary" className="shrink-0 mb-0" onClick={() => setShowEventModal(true)} data-id="element-1234">
                    <Plus className="w-4 h-4 mr-1.5" data-id="element-1235" />
                    New Event
                  </Button>}
              </div>
              {linkedEvent && <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100 text-sm flex items-center justify-between" data-id="element-1236">
                  <span className="text-emerald-700 font-medium" data-id="element-1237">
                    {linkedEvent.name}
                  </span>
                  <span className="font-bold text-emerald-800" data-id="element-1238">
                    +{formatCurrency(linkedEvent.amount, linkedEvent.currency)}/
                    {linkedEvent.type === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>}
            </div>

            {/* Closing & Acquisition Costs */}
            {!isExistingProperty && <div className="border-t border-slate-100 pt-4" data-id="element-1239">
                <h4 className="text-sm font-semibold text-slate-800 mb-1" data-id="element-1240">
                  Closing & Acquisition Costs
                </h4>
                <p className="text-xs text-slate-400 mb-4" data-id="element-1241">
                  One-time sunk costs that don't add property value — recorded
                  as a cash outflow.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-1242">
                  <div data-id="element-1243">
                    <Label data-id="element-1244">Notary Fees</Label>
                    <Input type="number" min="0" step="100" placeholder="0" value={notaryCosts} onChange={e => setNotaryCosts(e.target.value)} data-id="element-1245" />
                  </div>
                  <div data-id="element-1246">
                    <Label data-id="element-1247">Stamp Duty</Label>
                    <Input type="number" min="0" step="100" placeholder="0" value={stampDuty} onChange={e => setStampDuty(e.target.value)} data-id="element-1248" />
                  </div>
                  <div data-id="element-1249">
                    <Label data-id="element-1250">Transfer Tax</Label>
                    <Input type="number" min="0" step="100" placeholder="0" value={transferTax} onChange={e => setTransferTax(e.target.value)} data-id="element-1251" />
                  </div>
                  <div data-id="element-1252">
                    <Label data-id="element-1253">Other Costs</Label>
                    <Input type="number" min="0" step="100" placeholder="0" value={otherCosts} onChange={e => setOtherCosts(e.target.value)} data-id="element-1254" />
                  </div>
                </div>
                {totalClosingCosts > 0 && <>
                    <div className="mt-3 flex items-center justify-between px-3 py-2 bg-amber-50 rounded-lg border border-amber-100" data-id="element-1255">
                      <span className="text-xs font-semibold text-amber-700" data-id="element-1256">
                        Total Closing Costs
                      </span>
                      <span className="text-sm font-bold text-amber-800" data-id="element-1257">
                        {formatCurrency(totalClosingCosts, currency)}
                      </span>
                    </div>
                    {liquidHoldings.length > 0 && <div className="mt-3" data-id="element-1258">
                        <Label data-id="element-1259">Fund Closing Costs From</Label>
                        <Select searchable value={closingCostsFundedFromHoldingId} onChange={e => setClosingCostsFundedFromHoldingId(e.target.value)} options={[{
                  value: 'none',
                  label: "Don't deduct from holdings (manual / external)"
                }, ...liquidHoldings.map(h => ({
                  value: h.id,
                  label: `${h.name} (${formatCurrency(h.amount, h.currency)})`
                }))]} data-id="element-1260" />
                        {closingCostsFundedFromHoldingId !== 'none' && <p className="text-xs text-amber-600 mt-1.5" data-id="element-1261">
                            ⚠ {formatCurrency(totalClosingCosts, currency)}{' '}
                            will be deducted from this holding.
                          </p>}
                      </div>}
                  </>}
              </div>}

            {/* Exit Strategy */}
            <div className="border-t border-slate-100 pt-4" data-id="element-1262">
              <h4 className="text-sm font-semibold text-slate-800 mb-1" data-id="element-1263">
                Exit Strategy
              </h4>
              <p className="text-xs text-slate-400 mb-4" data-id="element-1264">
                How does this property factor into your long-term financial
                plan?
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1265">
                <div data-id="element-1266">
                  <Label data-id="element-1267">Strategy</Label>
                  <div className="flex bg-slate-100 p-1 rounded-lg mt-1" data-id="element-1268">
                    <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${realEstateStrategy === 'hold' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setRealEstateStrategy('hold')} data-id="element-1269">
                      🏠 Hold Long-term
                    </button>
                    <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${realEstateStrategy === 'sell' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setRealEstateStrategy('sell')} data-id="element-1270">
                      💰 Plan to Sell
                    </button>
                  </div>
                </div>
                {realEstateStrategy === 'sell' && <div data-id="element-1271">
                    <Label data-id="element-1272">Planned Sale Year (from now)</Label>
                    <Input type="number" min="0" step="1" placeholder="10" value={plannedSaleYear} onChange={e => setPlannedSaleYear(e.target.value)} data-id="element-1273" />
                    <p className="text-xs text-slate-400 mt-1" data-id="element-1274">
                      Sale proceeds (minus loan & CGT) enter your cash pool
                    </p>
                  </div>}
              </div>
              <div className="mt-3 p-3 rounded-lg border text-sm" data-id="element-1275">
                {realEstateStrategy === 'hold' ? <div className="bg-blue-50 border-blue-100 text-blue-700 p-3 rounded-lg" data-id="element-1276">
                    <p className="font-medium mb-1" data-id="element-1277">
                      📊 Equity only — not spendable
                    </p>
                    <p className="text-xs text-blue-600" data-id="element-1278">
                      This property appreciates and contributes to your net
                      worth, but won't be sold to fund retirement. Rental income
                      (if linked) still flows as cash.
                    </p>
                  </div> : <div className="bg-amber-50 border-amber-100 text-amber-700 p-3 rounded-lg" data-id="element-1279">
                    <p className="font-medium mb-1" data-id="element-1280">
                      💰 Investment exit — becomes cash
                    </p>
                    <p className="text-xs text-amber-600" data-id="element-1281">
                      At year {plannedSaleYear || '?'}, the appreciated value
                      (minus outstanding loan and capital gains tax) converts to
                      spendable cash. Appreciation and rental income stop after
                      sale.
                    </p>
                  </div>}
              </div>
            </div>

            {/* Live Equity Summary */}
            {currentValNum > 0 && <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200 flex flex-col gap-3" data-id="element-1282">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1" data-id="element-1283">
                  <Calculator className="w-4 h-4 text-blue-500" data-id="element-1284" />
                  Real Estate Summary Preview
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm" data-id="element-1285">
                  <div data-id="element-1286">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1287">
                      Current Value
                    </p>
                    <p className="font-medium text-slate-900" data-id="element-1288">
                      {formatCurrency(currentValNum, currency)}
                    </p>
                  </div>
                  <div data-id="element-1289">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1290">
                      Outstanding Loan
                    </p>
                    <p className="font-medium text-rose-600" data-id="element-1291">
                      {linkedLoan ? `-${formatCurrency(outstandingLoanBalance, currency)}` : 'None'}
                    </p>
                  </div>
                  <div data-id="element-1292">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1293">Net Equity</p>
                    <p className="font-bold text-emerald-700" data-id="element-1294">
                      {formatCurrency(netEquity, currency)}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mt-3 pt-3 border-t border-slate-200" data-id="element-1295">
                  {!isExistingProperty && totalClosingCosts > 0 && <div data-id="element-1296">
                      <p className="text-slate-500 text-xs mb-0.5" data-id="element-1297">
                        Sunk Costs
                      </p>
                      <p className="font-medium text-amber-700" data-id="element-1298">
                        -{formatCurrency(totalClosingCosts, currency)}
                      </p>
                    </div>}
                  <div data-id="element-1299">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1300">
                      Net Monthly Cashflow
                    </p>
                    <p className={`font-medium ${netMonthlyCashflow >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-1301">
                      {netMonthlyCashflow > 0 ? '+' : ''}
                      {formatCurrency(netMonthlyCashflow, currency)}
                    </p>
                  </div>
                  {!isExistingProperty && <div data-id="element-1302">
                      <p className="text-slate-500 text-xs mb-0.5" data-id="element-1303">
                        Total Cash Out
                      </p>
                      <p className="font-bold text-slate-800" data-id="element-1304">
                        {formatCurrency((Number(purchasePrice) || currentValNum) + totalClosingCosts, currency)}
                      </p>
                    </div>}
                </div>
              </div>}

            <div className="flex gap-3 mt-4" data-id="element-1305">
              <Button type="submit" className="flex-1" data-id="element-1306">
                <Building2 className="w-4 h-4 mr-2" data-id="element-1307" />
                {editingHolding ? 'Save Changes' : 'Add Real Estate Asset'}
              </Button>
              {editingHolding && onCancelEdit && <Button type="button" variant="secondary" onClick={onCancelEdit} data-id="element-1308">
                  Cancel
                </Button>}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Create Loan Modal */}
      <Modal isOpen={showLoanModal} onClose={() => setShowLoanModal(false)} title="Create New Loan" subtitle="This loan will be available to link to your property" data-id="element-1309">
        <LoanForm holdings={holdings} onAdd={loan => {
        if (onAddLoan) {
          const newId = onAddLoan(loan);
          setShowLoanModal(false);
          if (newId) {
            setLinkedLoanId(newId);
          }
        }
      }} partnerNames={partnerNames} initialValues={{
        name: name ? `${name} Mortgage` : '',
        currency,
        person,
        totalAmount: purchasePrice || currentValue || ''
      }} data-id="element-1310" />
      </Modal>

      {/* Create Cash Event Modal */}
      <Modal isOpen={showEventModal} onClose={() => setShowEventModal(false)} title="Create New Cash Event" subtitle="Create a recurring income event to link to this property" data-id="element-1311">
        <LiquidityEventForm onAdd={event => {
        if (onAddEvent) {
          const newId = onAddEvent(event);
          setShowEventModal(false);
          if (newId) {
            setLinkedEventId(newId);
          }
        }
      }} initialValues={{
        name: name ? `${name} Rental Income` : '',
        currency,
        type: 'monthly',
        isPositive: true,
        startYear: '0'
      }} data-id="element-1312" />
      </Modal>
    </>;
}